import { getLocalStorage, setLocalStorage } from '../../settingsUtil';

/** Inputs used to decide whether the partner migration prompt is eligible. */
export type PartnerPortalMigrationEligibilityInput = {
  email?: string;
  partner?: string;
};

/** Browser-persisted record for one signed-in user and partner deployment. */
export type PartnerPortalMigrationDismissal = {
  dismissedAt: string;
  email: string;
  partner: string;
};

const PORTAL_MIGRATION_STORAGE_PREFIX = 'PT_PARTNER_PORTAL_MIGRATION_DISMISSAL';
const DISMISSAL_TTL_MS = 24 * 60 * 60 * 1000;

const BOTH_PARTNER_PORTAL_CONTEXTS = {
  base: 1,
  partner: 1,
  merchant: 1,
} as const;

/**
 * Partner Administrator permissions consumed by Autheory in the new portal.
 * Keep this local migration template aligned with the new portal's user-role
 * definition until all partner users are managed there.
 */
export const partnerBetaPortalPermissions = {
  users: { base: 2, permissions: 2 },
  disputes: 2,
  invoices: { base: 2, resend: 1 },
  settings: { base: 2, fee_matrix: 1, funding_bank: 2 },
  developer: {
    base: 2,
    api_logs: 2,
    web_hooks: 2,
    secret_key: 2,
    native_apps: 2,
  },
  recurring: { base: 1, payment_method: 2 },
  settlements: 1,
  transactions: { base: 2, resend: 1 },
  payment_links: 2,
  authorizations: 2,
  export_payments: 1,
  manual_payments: 2,
  onboarding: 2,
  merchants: { base: 2, create: 2, configure: 1 },
  partner_portal: {
    base: 1,
    access_level: BOTH_PARTNER_PORTAL_CONTEXTS,
    scopes: {
      base: 1,
      edit_pricing_defaults: BOTH_PARTNER_PORTAL_CONTEXTS,
      edit_funding_configuration: BOTH_PARTNER_PORTAL_CONTEXTS,
      edit_risk_configuration: BOTH_PARTNER_PORTAL_CONTEXTS,
      request_funding_bank_change: BOTH_PARTNER_PORTAL_CONTEXTS,
    },
    onboarding: {
      base: 1,
      view_merchants_and_groups: 1,
      view_merchant_details: 1,
      create_merchant_accounts: 1,
      resend_applications: 1,
      send_password_resets: 1,
      edit_merchant_details: 1,
      access_onboarding_application: 1,
    },
    payments: {
      base: 1,
      view_flagged_payments: 1,
      review_flagged_payments: 1,
    },
    account: {
      base: 1,
      view_pricing: 1,
      view_funding_configuration: 1,
      view_risk_configuration: 1,
      view_compliance: 1,
      resend_invite_forgot_password: 1,
      edit_pricing_defaults: 1,
      edit_funding_configuration: 1,
      edit_risk_configuration: 1,
    },
  },
} as const;

/** Copy and actions shared with the merchant-portal beta announcement. */
export const partnerBetaPortalBannerMessage = 'Try the new portal (beta).';
export const PARTNER_BETA_PORTAL_LEARN_MORE_URL =
  'https://www.paytheory.com/blog/a-new-merchant-portal-experience';
export const partnerBetaPortalLearnMoreCta = 'Learn more';
export const partnerBetaPortalLoginCta = 'Log in to new portal';
export const partnerBetaPortalDismissCta = 'Not now';
export const partnerBetaPortalCreateErrorMessage =
  "We couldn't set up your new portal access. Please try again or contact support if the issue continues.";
export const partnerBetaPortalExistingAccountHeader =
  'New portal access is ready';
export const partnerBetaPortalExistingAccountMessage =
  'You already have access to the new portal. Please log in here:';
export const partnerBetaPortalMissingProfileMessage =
  "We couldn't create a new portal user because your profile is missing an email address. Please contact support.";

/**
 * Name attributes are optional on legacy partner logins. Provision those users
 * with a placeholder name instead of failing; they can update it in the new
 * portal.
 */
export const PARTNER_BETA_PORTAL_DEFAULT_FIRST_NAME = 'Partner';
export const PARTNER_BETA_PORTAL_DEFAULT_LAST_NAME = 'User';

/** Build the success notification shown after the invitation is requested. */
export const getPartnerBetaPortalEmailSentMessage = (email: string): string =>
  `Login email has been sent to ${email}`;

/** Resolve the matching white-label deployment for the new partner portal. */
export const getPartnerBetaPortalUrl = (
  partner = process.env.REACT_APP_PARTNER,
  stage = process.env.REACT_APP_STAGE,
): string => `https://${partner}.dashboard.${stage}.com`;

/** Recognize both legacy and structured duplicate-user errors from Autheory. */
export const isPartnerBetaPortalUserAlreadyExistsError = (
  errorMessage: string,
): boolean => {
  const normalizedMessage = errorMessage.toLowerCase();
  return (
    normalizedMessage.includes('already exists') ||
    normalizedMessage.includes('email_in_use')
  );
};

/** Normalize identity values before using them in browser-storage keys. */
export const normalizePartnerPortalMigrationValue = (
  value?: string | null,
): string => value?.trim().toLowerCase() ?? '';

/**
 * Key dismissals by both user and partner so one browser can safely access
 * multiple white-label partner deployments without leaking prompt state.
 */
export const getPartnerPortalMigrationDismissalKey = (
  email?: string,
  partner?: string,
): string | null => {
  const normalizedEmail = normalizePartnerPortalMigrationValue(email);
  const normalizedPartner = normalizePartnerPortalMigrationValue(partner);
  if (!normalizedEmail || !normalizedPartner) return null;

  return `${PORTAL_MIGRATION_STORAGE_PREFIX}:${normalizedEmail}:${normalizedPartner}`;
};

/** Build the local-storage record written by the “Not now” action. */
export const buildPartnerPortalMigrationDismissal = (
  email?: string,
  partner?: string,
  dismissedAt = new Date().toISOString(),
): PartnerPortalMigrationDismissal | null => {
  if (!email || !partner) return null;

  return { dismissedAt, email, partner };
};

/** Store a partner-scoped prompt dismissal in the current browser. */
export const setPartnerPortalMigrationDismissal = (
  dismissal: PartnerPortalMigrationDismissal,
): void => {
  const key = getPartnerPortalMigrationDismissalKey(
    dismissal.email,
    dismissal.partner,
  );
  if (!key) return;

  setLocalStorage(key)(JSON.stringify(dismissal));
};

/**
 * Clear every beta-prompt dismissal in this browser. This intentionally powers
 * a QA-only console hook rather than exposing a production UI control.
 */
export const resetAllPartnerPortalMigrationDismissals = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(PORTAL_MIGRATION_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch {
    // Browser privacy settings can disable localStorage; there is then no state to reset.
  }
};

/** Read and validate a prompt dismissal without allowing malformed state to render-break. */
export const getPartnerPortalMigrationDismissal = (
  email?: string,
  partner?: string,
): PartnerPortalMigrationDismissal | null => {
  const key = getPartnerPortalMigrationDismissalKey(email, partner);
  if (!key) return null;

  const value = getLocalStorage(key)();
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as PartnerPortalMigrationDismissal;
    const matchesUser =
      normalizePartnerPortalMigrationValue(parsed?.email) ===
      normalizePartnerPortalMigrationValue(email);
    const matchesPartner =
      normalizePartnerPortalMigrationValue(parsed?.partner) ===
      normalizePartnerPortalMigrationValue(partner);

    return parsed?.dismissedAt && matchesUser && matchesPartner ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Show the migration prompt only after both the signed-in user and partner
 * deployment are known. This avoids rendering an actionable prompt while
 * authentication is still hydrating.
 */
export const shouldShowPartnerPortalMigrationPrompt = ({
  email,
  partner,
}: PartnerPortalMigrationEligibilityInput): boolean => {
  if (!partner?.trim()) return false;

  // A missing email must not hide the banner: its primary action explains
  // that the signed-in profile needs support before it can be provisioned.
  if (!email?.trim()) return true;

  const dismissal = getPartnerPortalMigrationDismissal(email, partner);
  if (!dismissal) return true;

  const dismissedAt = new Date(dismissal.dismissedAt).getTime();
  return (
    !Number.isFinite(dismissedAt) ||
    Date.now() - dismissedAt >= DISMISSAL_TTL_MS
  );
};
